import {defineMessages} from 'react-intl'

import {SearchOffer} from '../types/SearchOffer'
import {getHasFreeCancellation} from './cancellationPenalties'
import {getMealsAmenities, getPrioritizedMeals} from './meal'
import {
  hasChargeLaterPayment,
  hasPayAtPropertyPayment
} from './offerPaymentMode'

/**
 * Translation dictionary containing labels for each possible room label, including the id and defaultMessage.
 */
export const roomLabels = defineMessages({
  freeCancellation: {
    id: 'cancellationPenalties.freeCancellation',
    defaultMessage: 'Free cancellation'
  },
  freeCancellationDeadline: {
    id: 'freeCancellationDeadline',
    defaultMessage: 'Free cancellation before {date}'
  },
  payLater: {
    id: 'paymentSchedule.payLater',
    defaultMessage: 'Pay later'
  },
  allInclusive: {
    id: 'mealType.allInclusive',
    defaultMessage: 'All inclusive'
  },
  breakfast: {
    id: 'mealType.breakfastIncluded',
    defaultMessage: 'Breakfast included'
  },
  lunch: {
    id: 'mealType.lunchIncluded',
    defaultMessage: 'Lunch included'
  },
  dinner: {
    id: 'mealType.dinnerIncluded',
    defaultMessage: 'Dinner included'
  },
  fullBoard: {
    id: 'meals.fullBoard',
    defaultMessage: 'Breakfast, lunch and dinner included'
  },
  halfBoard: {
    id: 'meals.halfBoard',
    defaultMessage: 'Breakfast and dinner included'
  },
  meals: {
    id: 'mealType.allInclusive',
    defaultMessage: 'All inclusive'
  },
  yourSelection: {
    id: 'yourSelection',
    defaultMessage: 'Your selection'
  },
  bookNowPayLater: {
    id: 'payment.payLater',
    defaultMessage: 'Book now, pay later'
  },
  chargeLater: {
    id: 'chargeLater.label',
    defaultMessage: 'Pay now or later'
  }
})

export interface GetRoomLabelsProps {
  // Whether the booking is split.
  isSplitBooking?: boolean
  // The offer to get the labels for.
  offer: SearchOffer
  // Whether to show the free cancellation date.
  shouldShowFreeCancellationDate?: boolean
  // Whether to show the "Your Choice" label.
  shouldShowYourChoice: boolean
}

const getFreeCancellationLabel = ({
  offer,
  shouldShowFreeCancellationDate
}: {
  offer: GetRoomLabelsProps['offer']
  shouldShowFreeCancellationDate: GetRoomLabelsProps['shouldShowFreeCancellationDate']
}) => {
  const hasFreeCancellation = getHasFreeCancellation(
    offer?.cancellationPenalties
  )

  if (hasFreeCancellation && shouldShowFreeCancellationDate) {
    return [roomLabels.freeCancellationDeadline]
  }

  if (hasFreeCancellation) {
    return [roomLabels.freeCancellation]
  }

  return []
}

const getChargeLaterLabel = ({
  offer,
  isSplitBooking
}: {
  offer: GetRoomLabelsProps['offer']
  isSplitBooking: GetRoomLabelsProps['isSplitBooking']
}) => {
  return hasChargeLaterPayment(offer, isSplitBooking)
    ? [roomLabels.bookNowPayLater]
    : []
}

const getMealsLabels = (offer: GetRoomLabelsProps['offer']) => {
  const meals = getMealsAmenities(offer)

  return getPrioritizedMeals(meals).map(meal => {
    return meal && roomLabels[meal]
  })
}

const getMessageObjects = ({
  offer,
  isSplitBooking = false,
  shouldShowYourChoice,
  shouldShowFreeCancellationDate
}: GetRoomLabelsProps) => {
  const yourChoiceLabel = shouldShowYourChoice ? [roomLabels.yourSelection] : []

  const freeCancellationLabel = getFreeCancellationLabel({
    offer,
    shouldShowFreeCancellationDate
  })

  const payLaterLabel = hasPayAtPropertyPayment(offer)
    ? [roomLabels.payLater]
    : []

  const chargeLaterLabel = getChargeLaterLabel({
    offer,
    isSplitBooking
  })

  const translatedMealsLabels = getMealsLabels(offer)

  return [
    ...yourChoiceLabel,
    ...freeCancellationLabel,
    ...payLaterLabel,
    ...chargeLaterLabel,
    ...translatedMealsLabels
  ]
}

/**
 * Get the room labels for a given offer.
 * @param  GetRoomLabelsProps - The properties to get the room labels for.
 * @param  GetRoomLabelsProps.offer - The offer object containing room details.
 * @param  GetRoomLabelsProps.shouldShowFreeCancellationDate - Whether to display the free cancellation date.
 * @param  GetRoomLabelsProps.isSplitBooking - Whether the booking is split across multiple reservations.
 * @param  GetRoomLabelsProps.shouldShowYourChoice - Whether to highlight the user's choice.
 * @returns An array containing labels for each room offer, including the id and defaultMessage.
 */
export const getRoomLabels = ({
  offer,
  shouldShowFreeCancellationDate = false,
  isSplitBooking = false,
  shouldShowYourChoice
}: GetRoomLabelsProps) => {
  const labelMessagesList = getMessageObjects({
    offer,
    shouldShowFreeCancellationDate,
    isSplitBooking,
    shouldShowYourChoice
  })

  return labelMessagesList
}
